import Done from '@mui/icons-material/Done';
import MuiBox from '@mui/material/Box';
import MuiTypography from '@mui/material/Typography';
import { AxiosError } from 'axios';
import React, { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';

import { STRUCTURE_HELPER_TEXT } from './AddEditStructureLocation.data';
import { AddEditStructureLocationProps, SearchParams } from './AddEditStructureLocation.props';
import {
  ModalBox,
  ModalContent,
  ModalFooter,
  LocationSearchBox,
  LoadingWrapper,
} from './AddEditStructureLocation.styles';
import { LocationMergeAccordianFragment } from './LocationMergeAccordianFragment';
import { PartAccordianFragment } from './PartsAccordianFragment';
import { ServiceAccordianFragment } from './ServiceAccordianFragment';
import { ShipmentAccordianFragment } from './ShipmentAccordianFragment';
import { StructureCapabilitiesPanel } from './StructureCapabilitiesPanel';
import { StructureDetailShell } from './StructureDetailShell';
import { StructureDetailsTabBody } from './StructureDetailsTabBody';
import { toSummaryTags } from './structureSummary';
import {
  getStructureSectionVisibility,
  getVisibleStructureTabs,
  hasStructureLocationContent,
  StructureTabKey,
} from './structureTabs';
import { StructureTabSection } from './StructureTabSection';
import { StructureTabStrip } from './StructureTabStrip';
import { StructureZoneBanner } from './StructureZoneBanner';
import { useStructureActiveTab } from './useStructureActiveTab';
import { useStructureUnsavedChanges } from './useStructureUnsavedChanges';
import {
  ActionPermissions,
  CustomPermission,
  LocationNodeData,
  NodeType,
  ParentSelectionType,
  SiteSchemaDropdown,
  SizeProps,
  StructureAttribute,
  StructureBasicData,
  StructureEditPermissionSet,
  StructureHelperText,
  StructureLayoutData,
  StructureTypeResponse,
  Tag,
} from '../../../@types';
import { PrintLabelEntityTypes } from '../../../@types/print-label.type';
import { Button, ButtonVariantProps } from '../../../components/atoms/Button';
import { CloseIcon } from '../../../components/atoms/CloseIcon';
import { LiveSearchBox, LiveSearchListItem } from '../../../components/atoms/LiveSearchBox';
import { Loader, LoaderColorProps } from '../../../components/atoms/Loader';
import { Modal } from '../../../components/atoms/Modal';
import { PageContainer } from '../../../components/atoms/PageContainer';
import { Panel } from '../../../components/atoms/Panel';
import { PermissionLink } from '../../../components/atoms/PermissionLink';
import { DropdownItem, SelectMenu } from '../../../components/atoms/SelectMenu';
import { TextField } from '../../../components/atoms/TextField';
import {
  ToggleButtonGroup,
  ToogleButtonGroupColor,
} from '../../../components/atoms/ToogleButtonGroup';
import {
  StyledCloseIcon,
  StyledPeopleAltOutlinedIcon,
  StyledSearchIcon,
} from '../../../components/atoms/ToogleButtonGroup/ToogleButtonGroup.styles';
import { BrandSelector } from '../../../components/molecules/BrandSelector';
import { ConfirmationPopup } from '../../../components/molecules/ConfirmationPopup';
import { ContactData } from '../../../components/molecules/Contact';
import { LocationSearchItem } from '../../../components/molecules/LocationSearchItem';
import { PrintLabel } from '../../../components/molecules/PintLabel';
import { StructureChild, ChildrenDataType } from '../../../components/molecules/StructureChild';
import { StructureSummaryBar } from '../../../components/molecules/StructureSummaryBar';
import { TradingHoursData } from '../../../components/molecules/TradingHours';
import {
  CapacityData,
  CapacityHourTypes,
  DropdownItem as CapacityServiceTypes,
} from '../../../components/organisms/Capacity/Capacity.props';
import { AddressDataType } from '../../../components/organisms/StructureAddress';
import { StructureLayout } from '../../../components/organisms/StructureLayout';
import {
  AutocompleteTagType,
  getNewTagsMapped,
  getSelectedExistingTagsMapped,
  mapSavedTags,
} from '../../../components/organisms/Tags';
import { ContactDataType } from '../../../components/templates/StructureAddressContactLayout';
import { PLATFORM_NAME } from '../../../configs/common';
import {
  EntityType,
  PropertyType,
  PropertyType as PropertyTypeEnum,
  StructureTypeCategory,
} from '../../../configs/enums';
import { isUserHasPermission } from '../../../configs/permissions';
import {
  HAS_STRUCTURE_EDIT_PERMISSIONS,
  SAMPLE_CAPACITY_DATA,
  SAMPLE_EDIT_STRUCTURE_PERMISSION_SET,
  SAMPLE_STRUCTURE_LAYOUT,
  SITE_SCHEMA_SAMPLE_DATA,
} from '../../../constants/structure';
import {
  CENTRAL_SUPPLY_WAREHOUSE_CONFIRM_DESCRIPTION,
  CENTRAL_SUPPLY_WAREHOUSE_CONFIRM_TITLE,
  CENTRAL_SUPPLY_WAREHOUSE_SAVE_ERROR,
} from '../../../constants/structureType';
import { TabHandler } from '../../../handlers/TabHandler';
import { useSearchNodeByParentId } from '../../../queries/live-search-query';
import { useGetAllServiceTypes } from '../../../queries/service-type-query';
import {
  useCreateStructure,
  useGetStructuresByTypeCategoryCodeAndTypeCode,
  useGetNodeChildrenByNodeType,
  useGetNodeById,
  useUpdateStructure,
  useGetNodeChildren,
  useUpdateStructureBasicData,
  useGetNodeSubTree,
  useGetSiteSchemasByClientContract,
} from '../../../queries/structure-query';
import {
  useGetStructureTypesByCategoryCode,
  useGetStructureTypesByCode,
} from '../../../queries/structure-type-query';
import { useGetAllTags, useAddTags } from '../../../queries/tags-query';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { getActiveToggleButton, getLocalDateString, isDropDownEmpty } from '../../../utils/common';
import { displayNodeTypeName } from '../../../utils/node-type';
import { getHasCodeForNodeSearch } from '../../../utils/services';
import {
  getSortedStructureTypes,
  getServiceTypesDropdownData,
  getNodeAttributes,
  genarateURL,
  getWarningsOnlyForStructures,
  getWarningsForAddressContacts,
  getUpdateStructurePageUrl,
} from '../../../utils/structure';
import { toast } from '../../../utils/toast';
import { NotFoundError } from '../../Error/NotFound';

export const AddEditStructureLocation: React.FC<AddEditStructureLocationProps> = ({
  pageType,
  locationType,
  structureId,
  locationText,
  mainLocationNode,
  onSetCreatedLocation,
  setMainLocationNode,
  setSubLocationNode,
  setLocationText,
  setOpenLocationCreateUpdatePopup,
}) => {
  const { id: paramId } = useParams();
  const id = structureId || paramId;
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const parentId = searchParams.get(SearchParams.ParentId);
  const parentName = searchParams.get(SearchParams.ParentName);
  const nodeTypeCode = searchParams.get(SearchParams.NodeTypeCode);
  const preferedNodeTypes = searchParams.getAll(SearchParams.PreferedNodeType);
  const selectedClientId = searchParams.get(SearchParams.ClientId);
  const selectedContractId = searchParams.get(SearchParams.ContractId);
  let isNew = id === 'create';

  const [structureLayoutData, setStructureLayoutData] = useState<StructureLayoutData>({
    ...SAMPLE_STRUCTURE_LAYOUT,
    name: locationText || '',
  });
  const [structureLayoutSchemaType, setStructureLayoutSchemaType] =
    useState<Array<SiteSchemaDropdown>>(SITE_SCHEMA_SAMPLE_DATA);
  const [contactData, setContactData] = useState<ContactData[]>([]);
  const [addressData, setAddressData] = useState<AddressDataType[]>([]);
  const [tradingHoursData, setTradingHoursData] = useState<TradingHoursData[]>([]);
  const [capacityData, setCapacityData] = useState<CapacityData[]>([]);
  const [structureAddressDataset, setStructureAddressDataset] = useState<AddressDataType[]>([]);
  const [structureContactDataset, setStructureContactDataset] = useState<ContactDataType[]>([]);
  const [editContactData, setEditContactData] = useState<ContactData>();
  const [editAddressData, setEditAddressData] = useState<AddressDataType>();
  const [selectedSystemTags, setSelectedSystemTags] = useState<(AutocompleteTagType | string)[]>(
    []
  );
  const [newCreatedTags, setNewCreatedTags] = useState<Tag[]>([]);
  const [structureTypes, setStructureTypes] = useState<Array<StructureTypeResponse>>([]);
  const [selectedStructureType, setSelectedStructureType] = useState(nodeTypeCode || '-1');
  const [propertyTypes, setPropertyTypes] = useState(['']);
  const [openParentSelector, setOpenParentSelector] = useState(false);
  const [openLocationSearch, setOpenLocationSearch] = useState(false);
  const [parentType, setParentType] = useState<string>(
    parentId ? ParentSelectionType.Search : ParentSelectionType.None
  );
  const [queryNodeType, setQueryNodeType] = useState(NodeType.Client);
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [selectedParentId, setSelectedParentId] = useState('');
  const [selectedParenHierarchy, setSelectedParenHierarchy] = useState<string | ReactNode>('');
  const [clients, setClients] = useState<DropdownItem[]>([]);
  const [contracts, setContracts] = useState<DropdownItem[]>([]);
  const [serviceTypes, setServiceTypes] = useState<CapacityServiceTypes[]>([]);
  const [capacityClientsDefault, setCapacityClientsDefault] = useState<DropdownItem[]>([]);
  const [capacityContractsDefault, setCapacityContractsDefault] = useState<DropdownItem[]>([]);
  const [brands, setBrands] = useState<DropdownItem[]>([]);
  const [isActive, setIsActive] = useState(false);
  const [nodeChildren, setNodeChildren] = useState<ChildrenDataType[]>([]);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [childNodebasicData, setChildNodebasicData] = useState<StructureBasicData>();
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [permissions, setPermissions] = useState<CustomPermission[]>();
  const [locationName, setLocationName] = React.useState('');
  const [locationId, setLocationId] = React.useState<number>(parseInt(parentId || '0'));
  const [areDependenciesLoaded, setAreDependenciesLoaded] = useState(false);
  const [isSiteSchemaEnabled, setIsSiteSchemaEnabled] = useState(false);
  const [isReferenceNumbersEnabled, setIsReferenceNumbersEnabled] = useState(false);
  const [capacityDefaultValue, setCapacityDefaultValue] =
    React.useState<CapacityData>(SAMPLE_CAPACITY_DATA);
  const [clientId, setClientId] = useState('');
  const [contractId, setContractId] = useState('');
  const [checkedGlobalReturns, setCheckedGlobalReturns] = useState(false);
  const [checkedCentralSupplyWarehouse, setCheckedCentralSupplyWarehouse] = useState(false);
  // Confirm before switching the marker on: it moves the depot replenishment
  // roll-up for the whole organisation.
  const [confirmCentralSupplyWarehouse, setConfirmCentralSupplyWarehouse] = useState(false);
  const [disableSave, setDisableSave] = useState(false);
  const [contactForm, setContactForm] = useState<unknown[]>([]);
  const [addressForm, setAddressForm] = useState<unknown[]>([]);
  const [parentNodeName, setParentNodeName] = useState<string | ReactNode>('');
  const [helperText, setHelperText] = useState<StructureHelperText>(STRUCTURE_HELPER_TEXT);
  const [validate, setValidate] = useState(false);
  const [, setParentSelect] = useState(false);
  const [structureEditPermissions, setStructureEditPermissions] =
    useState<StructureEditPermissionSet>(SAMPLE_EDIT_STRUCTURE_PERMISSION_SET);
  const [clearClientHierarchy, setClearClientHierarchy] = useState(false);

  const { data: structureTypesData } = useGetStructureTypesByCategoryCode(
    StructureTypeCategory.Operational
  );
  const createStructure = useCreateStructure();
  const updateStructure = useUpdateStructure();
  const getNodeData = useGetNodeById(id || '', StructureTypeCategory.Operational);
  const getStructuresByTypeCategoryCodeAndTypeCode = useGetStructuresByTypeCategoryCodeAndTypeCode(
    StructureTypeCategory.System,
    NodeType.Client
  );
  const getStructuresByTypeCategoryCodeAndContractTypeCode =
    useGetStructuresByTypeCategoryCodeAndTypeCode(StructureTypeCategory.System, NodeType.Contract);
  const getNodeChildrenByNodeType = useGetNodeChildrenByNodeType(selectedParentId, queryNodeType);
  const getNodeChildren = useGetNodeChildren(id || '', true);
  const updateStructureBasicData = useUpdateStructureBasicData();
  const getNodeSubTree = useGetNodeSubTree(id || '');
  const getStructureTags = useGetAllTags(EntityType.TYPE_STRUCTURELOCATIONS);
  const addTagsQuery = useAddTags();
  const getUserPermissionsQuery = useGetUserSystemPermissions();
  // `locationId` is this page's own "no parent selected yet" sentinel — it's
  // reset to NaN on mount (and whenever the location-search picker is
  // cleared) and only ever set to a real id once the user interacts with
  // that picker. `NaN.toString()` is the truthy string "NaN", so the old
  // `|| '0'` fallback never caught it, sending `GET /nodes/NaN/subtree`
  // (enabled: nodeId !== '0' doesn't know "NaN" from a real id either).
  const getNodeSubTreeQuery = useGetNodeSubTree(
    Number.isNaN(locationId) ? '0' : locationId.toString()
  );
  const searchNodeByParentId = useSearchNodeByParentId();
  const getServiceTypes = useGetAllServiceTypes();
  const getStructureTypesByCode = useGetStructureTypesByCode(selectedStructureType);
  const getSiteSchemasByClientContract = useGetSiteSchemasByClientContract(clientId, contractId);

  TabHandler(
    isNew
      ? `Create Structure | ${PLATFORM_NAME}`
      : `Structure: ${id} | Structures | ${PLATFORM_NAME}`
  );

  useEffect(() => {
    getUserPermissionsQuery.data && setPermissions(getUserPermissionsQuery.data);
  }, [getUserPermissionsQuery.data]);

  useEffect(() => {
    if (permissions) {
      const hasPermissions = Object.fromEntries(
        Object.entries(HAS_STRUCTURE_EDIT_PERMISSIONS).map(([key, value]) => [
          key,
          isUserHasPermission(value, permissions),
        ])
      );

      setStructureEditPermissions(hasPermissions);
    }
  }, [permissions]);

  useEffect(() => {
    getServiceTypes.data && setServiceTypes(getServiceTypesDropdownData(getServiceTypes.data));
  }, [getServiceTypes.data]);

  useEffect(() => {
    if (capacityData) {
      getStructuresByTypeCategoryCodeAndTypeCode.refetch();
      getStructuresByTypeCategoryCodeAndContractTypeCode.refetch();
    }
    // getStructuresByTypeCategoryCodeAndTypeCode / ...ContractTypeCode are React Query
    // result objects that get a new identity on most renders; adding them here would
    // re-fire this effect (and re-refetch) on unrelated re-renders instead of only
    // when capacityData changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacityData]);

  useEffect(() => {
    if (
      serviceTypes.length > 0 &&
      capacityClientsDefault.length > 0 &&
      capacityContractsDefault.length > 0
    ) {
      setCapacityDefaultValue({
        type: CapacityHourTypes.AllDays,
        checkInventory: true,
        allowOveride: true,
        max: '4',
        itemsData: serviceTypes,
        clientsData: capacityClientsDefault,
        contractsData: capacityContractsDefault,
      });
      setAreDependenciesLoaded(true);
    }
  }, [serviceTypes, capacityClientsDefault, capacityContractsDefault]);

  useEffect(() => {
    if (locationId) {
      getNodeSubTreeQuery.refetch();
    }
    // getNodeSubTreeQuery is a React Query result object with an unstable identity
    // across renders; adding it here would re-fire this refetch on every render
    // rather than only when locationId changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  useEffect(() => {
    if (getNodeSubTreeQuery.data) {
      const clientNode = getNodeSubTreeQuery.data.find(
        (item) => item.nodeTypeCode === NodeType.Client
      );
      const parentNode = getNodeSubTreeQuery.data.find((item) => item.id === locationId);

      setParentNodeName(
        parentNode ? (
          <PermissionLink
            permission="structure.edit.view_structure_location"
            href={`/structure/${parentNode.id}`}
          >
            {parentNode.name}
          </PermissionLink>
        ) : (
          ''
        )
      );

      if (clientNode) {
        setClients([
          {
            value: clientNode.id.toString(),
            label: clientNode.name,
            disabled: !clientNode.isActive,
          },
        ]);
      }

      const contractNode = getNodeSubTreeQuery.data.find(
        (item) => item.nodeTypeCode === NodeType.Contract
      );

      if (contractNode) {
        setContracts([
          {
            value: contractNode.id.toString(),
            label: contractNode.name,
            disabled: !contractNode.isActive,
          },
        ]);
      }

      const brandNode = getNodeSubTreeQuery.data.find(
        (item) => item.nodeTypeCode === NodeType.Brand
      );

      if (brandNode) {
        setBrands([
          { value: brandNode.id.toString(), label: brandNode.name, disabled: !brandNode.isActive },
        ]);
      }

      setSelectedParenHierarchy(
        clientNode ? (
          <>
            <PermissionLink
              permission="configure.client_structure.clients_or_contracts.edit"
              href={`/configure/clients/${clientNode?.nodeTypeCode}/${clientNode?.id}`}
            >
              {clientNode?.name}
            </PermissionLink>
            /
            <PermissionLink
              permission="configure.client_structure.clients_or_contracts.edit"
              href={`/configure/clients/${contractNode?.nodeTypeCode}/${contractNode?.id}`}
            >
              {contractNode?.name}
            </PermissionLink>
            /{brandNode?.name}
          </>
        ) : (
          '-'
        )
      );
      setSelectedBrandId(locationId?.toString() || '');
    }
    // locationId is a plain useState number, so adding it is nominally "safe" by
    // identity, but this effect's whole job is to react to *new* sub-tree data —
    // getNodeSubTreeQuery's query key is itself derived from locationId (see the
    // NaN-sentinel comment above), so locationId and getNodeSubTreeQuery.data don't
    // always settle in the same render (the id changes a render before the refetch
    // it triggers resolves). Adding locationId would make this effect also run
    // against the *previous* sub-tree data at the moment of selection, re-deriving
    // parent/client/contract/brand from a stale response. Given this exact
    // locationId/NaN sentinel has already caused one production bug in this file,
    // we key this effect on the data arrival only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getNodeSubTreeQuery.data]);

  useEffect(() => {
    if (!isNew) {
      setStructureContactDataset([]);
      setStructureAddressDataset([]);
      setTradingHoursData([]);
      setCapacityData([]);
      setStructureLayoutData(SAMPLE_STRUCTURE_LAYOUT);
      setCheckedGlobalReturns(false);
      setCheckedCentralSupplyWarehouse(false);
      setSelectedBrandId('');
      setSelectedParenHierarchy('');
      setLocationId(NaN);
      setLocationName('');

      getNodeData.refetch();
      getNodeChildren.refetch();
    }
    // getNodeData / getNodeChildren are React Query result objects with unstable
    // identity across renders; adding them here would re-fire this reset+refetch on
    // unrelated re-renders instead of only when isNew/id change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, id]);

  useEffect(() => {
    getStructureTags.data && setAllTags(getStructureTags.data);
  }, [getStructureTags.data]);

  useEffect(() => {
    if (getNodeData.data && areDependenciesLoaded) {
      const data = getNodeData.data;
      setSelectedStructureType(data.nodeTypeCode);
      setSelectedSystemTags(mapSavedTags(data.tags));

      const relevantWarnings = getWarningsOnlyForStructures(data.warnings);
      relevantWarnings.forEach((warning) => toast.warning(warning.message));
      setIsActive(data.isActive);

      if (data.parentNodeId) {
        getNodeSubTree.refetch();
      }

      setStructureLayoutData({
        id: data.id,
        name: data.name,
        notes: data.notes,
        selectedTags: [],
        newTags: [],
        referenceNo1: '',
        referenceNo2: '',
        referenceNo3: '',
        siteSchemaId: '0',
        zone: data.zone,
        region: data.regionType,
      });

      if (propertyTypes.length) {
        setStructureContactDataset([]);
        setStructureAddressDataset([]);
        setTradingHoursData([]);
        setCapacityData([]);
        setCheckedGlobalReturns(false);
        setCheckedCentralSupplyWarehouse(false);

        data.attributes?.forEach((attribute: StructureAttribute) => {
          const { id, propertyTypeCode, value, updatedAt } = attribute;

          if (propertyTypeCode === PropertyTypeEnum.Contact && value?.isActive) {
            setStructureContactDataset((prev) => [...prev, { ...value, id: id }]);
          }
          if (propertyTypeCode === PropertyTypeEnum.Address && value?.isActive) {
            setStructureAddressDataset((prev) => [
              ...prev,
              {
                ...value,
                id: id,
                name: value.addressName,
              },
            ]);
          }
          if (propertyTypeCode === PropertyTypeEnum.TradingHours) {
            setTradingHoursData((prev) => [...prev, { ...value, id: id }]);
          }
          if (propertyTypeCode === PropertyTypeEnum.Allocations) {
            setCapacityData((prev) => [
              ...prev,
              {
                ...value,
                id,
                itemsData: serviceTypes,
                clientsData: capacityClientsDefault,
                contractsData: capacityContractsDefault,
              },
            ]);
          }
          if (propertyTypeCode === PropertyTypeEnum.ReferenceNumbers) {
            setStructureLayoutData((prev) => ({
              ...prev,
              refAttribId: id,
              referenceNo1: value.referenceNo1,
              referenceNo2: value.referenceNo2,
              referenceNo3: value.referenceNo3,
            }));
          }
          if (propertyTypeCode === PropertyTypeEnum.SiteSchema) {
            setStructureLayoutData((prev) => ({
              ...prev,
              siteSchemaAttribId: id,
              siteSchemaId: value.siteSchemaId,
            }));
          }
          if (propertyTypeCode === PropertyTypeEnum.MostRecentPresentation) {
            setStructureLayoutData((prev) => ({
              ...prev,
              mostRecentPresentation: {
                description: value?.description,
                date: getLocalDateString(updatedAt ?? ''),
              },
            }));
          }
          if (
            propertyTypeCode === PropertyTypeEnum.GlobalReturns &&
            isUserHasPermission(ActionPermissions.Configure_Structure_Type_Edit, permissions) &&
            propertyTypes.includes(PropertyType.GlobalReturns)
          ) {
            setCheckedGlobalReturns(value.globalReturns);
          }
          if (
            propertyTypeCode === PropertyTypeEnum.CentralSupplyWarehouse &&
            propertyTypes.includes(PropertyType.CentralSupplyWarehouse)
          ) {
            setCheckedCentralSupplyWarehouse(!!value?.centralSupplyWarehouse);
          }
        });
      }
    }
    // getNodeSubTree is a React Query result object with unstable identity across
    // renders, so it can't be added directly. capacityClientsDefault /
    // capacityContractsDefault / serviceTypes / permissions are plain useState
    // values and are individually stable, but this effect resets
    // structureContactDataset/structureAddressDataset/tradingHoursData/capacityData
    // to [] and rebuilds them from data.attributes every time it runs — if it fired
    // whenever one of those four changed (which can happen independently of the
    // node actually reloading, e.g. a later permissions/service-types refetch),
    // it would silently discard any in-progress, unsaved edits the user has made to
    // those sections. Keeping this scoped to node-load/dependencies-ready/
    // propertyTypes changes only is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getNodeData.data, areDependenciesLoaded, propertyTypes]);

  useEffect(() => {
    selectedStructureType && getStructureTypesByCode.refetch();
  }, [selectedStructureType]);

  useEffect(() => {
    selectedClientId && setClientId(selectedClientId);
    selectedContractId && setContractId(selectedContractId);
  }, [selectedClientId, selectedContractId]);

  useEffect(() => {
    if (clientId && contractId) {
      getSiteSchemasByClientContract.refetch();
    }
  }, [clientId, contractId]);

  useEffect(() => {
    getSiteSchemasByClientContract.data &&
      setStructureLayoutSchemaType(getSiteSchemasByClientContract.data);
  }, [getSiteSchemasByClientContract.data]);

  // Use for Site schema field enable / disable logic.
  useEffect(() => {
    if (getStructureTypesByCode.data) {
      // Check selected structure type has site schema propertyType
      const propertyTypeCheck = getStructureTypesByCode.data.propertyTypes?.some(
        (item) => item.propertyType === PropertyTypeEnum.SiteSchema
      );
      const referenceNumbersEnabled = getStructureTypesByCode.data.propertyTypes?.some(
        (item) => item.propertyType === PropertyTypeEnum.ReferenceNumbers
      );
      // Check if node is under client hierarchy and client has schema records configured for enabling site schema dropdown
      if (
        propertyTypeCheck &&
        parentType === NodeType.Client &&
        getSiteSchemasByClientContract.data?.length
      ) {
        setIsSiteSchemaEnabled(true);
        setStructureLayoutSchemaType(getSiteSchemasByClientContract.data);
      } else {
        setIsSiteSchemaEnabled(false);
        setStructureLayoutSchemaType([]);
      }

      setIsReferenceNumbersEnabled(referenceNumbersEnabled);
    }
  }, [getStructureTypesByCode.data, getSiteSchemasByClientContract.data, parentType]);

  const handleChange = (_event: unknown, type: string) => {
    if (!parentId) {
      type && setParentType(type);
      if (type === NodeType.Client) {
        if (isNew || (!isNew && selectedBrandId === null)) {
          getStructuresByTypeCategoryCodeAndTypeCode.refetch();
        }
      }
    }
  };

  useEffect(() => {
    if (getNodeSubTree.data) {
      const parentNode = getNodeSubTree.data.find(
        (node) => node.id === Number(getNodeData?.data?.parentNodeId)
      );
      if (parentNode?.nodeTypeCode !== NodeType.Brand) {
        getNodeData.data?.clientId && setClientId(getNodeData.data.clientId.toString());
        getNodeData.data?.contractId && setContractId(getNodeData.data.contractId.toString());
        parentNode && setSelectedBrandId(parentNode.id.toString());
        setSelectedParenHierarchy(
          (
            <>
              <PermissionLink
                permission="structure.edit.view_structure_location"
                href={`/structure/${parentNode?.id}`}
              >
                {parentNode?.name}
              </PermissionLink>
            </>
          ) || ''
        );
      } else {
        setSelectedBrandId(getNodeData?.data?.parentNodeId || '');
        const clientNode = getNodeSubTree.data.find(
          (item) => item.nodeTypeCode === NodeType.Client
        );

        if (clientNode) {
          setClientId(clientNode.id.toString());
          setClients([
            {
              value: clientNode.id.toString(),
              label: clientNode.name,
              disabled: !clientNode.isActive,
            },
          ]);
        }

        const contractNode = getNodeSubTree.data.find(
          (item) => item.nodeTypeCode === NodeType.Contract
        );

        if (contractNode) {
          setContractId(contractNode.id.toString());
          setContracts([
            {
              value: contractNode.id.toString(),
              label: contractNode.name,
              disabled: !contractNode.isActive,
            },
          ]);
        }

        const brandNode = getNodeSubTree.data.find((item) => item.nodeTypeCode === NodeType.Brand);
        setSelectedParenHierarchy(
          <>
            <PermissionLink
              permission="configure.client_structure.clients_or_contracts.edit"
              href={`/configure/clients/${clientNode?.nodeTypeCode}/${clientNode?.id}`}
            >
              {clientNode?.name}
            </PermissionLink>
            /
            <PermissionLink
              permission="configure.client_structure.clients_or_contracts.edit"
              href={`/configure/clients/${contractNode?.nodeTypeCode}/${contractNode?.id}`}
            >
              {contractNode?.name}
            </PermissionLink>
            /{brandNode?.name}
          </>
        );
        setParentType(ParentSelectionType.Client);
      }
    }
  }, [getNodeSubTree.data, getNodeData.data]);

  useEffect(() => {
    if (getStructuresByTypeCategoryCodeAndTypeCode.data) {
      setClients(
        getStructuresByTypeCategoryCodeAndTypeCode.data.map((item) => ({
          value: item.id.toString(),
          label: item.name,
          disabled: !item.isActive,
        }))
      );
      setCapacityClientsDefault([
        { value: 'any', label: 'Any' },
        ...getStructuresByTypeCategoryCodeAndTypeCode.data.map((item) => ({
          value: item.id.toString(),
          label: item.name,
        })),
      ]);
    }
  }, [
    getStructuresByTypeCategoryCodeAndTypeCode.data,
    getStructuresByTypeCategoryCodeAndTypeCode.isRefetching,
  ]);

  useEffect(() => {
    if (getStructuresByTypeCategoryCodeAndContractTypeCode.data) {
      setCapacityContractsDefault(
        getStructuresByTypeCategoryCodeAndContractTypeCode.data.map((item) => ({
          value: item.id.toString(),
          label: item.name,
        }))
      );
      setCapacityContractsDefault([
        { value: 'any', label: 'Any' },
        ...getStructuresByTypeCategoryCodeAndContractTypeCode.data.map((item) => ({
          value: item.id.toString(),
          label: item.name,
        })),
      ]);
    }
  }, [getStructuresByTypeCategoryCodeAndContractTypeCode.data]);

  useEffect(() => {
    if (getNodeChildren.data) {
      setNodeChildren(
        getNodeChildren.data.map((item) => ({
          id: item.id,
          name: item.name,
          type: item.nodeTypeCode,
          isActive: item.isActive,
          typeName: displayNodeTypeName(item.nodeType.name),
        }))
      );
    }
  }, [getNodeChildren.data]);

  useEffect(() => {
    if (selectedParentId && queryNodeType) {
      if (
        (isNew && queryNodeType !== NodeType.Client) ||
        (!isNew && !selectedBrandId) ||
        (!isNew && queryNodeType === NodeType.Brand)
      ) {
        getNodeChildrenByNodeType.refetch();
      }
    }
  }, [selectedParentId, queryNodeType]);

  useEffect(() => {
    if (getNodeChildrenByNodeType.data) {
      if (queryNodeType === NodeType.Contract) {
        const contractMenuList = getNodeChildrenByNodeType.data.map((item) => ({
          value: item.id.toString(),
          label: item.name,
          disabled: !item.isActive,
        }));
        setContracts(contractMenuList);
      } else if (queryNodeType === NodeType.Brand) {
        const brandMenuList = getNodeChildrenByNodeType.data.map((item) => ({
          value: item.id.toString(),
          label: item.name,
          disabled: !item.isActive,
        }));
        setBrands(brandMenuList);
      }
    }
  }, [getNodeChildrenByNodeType.data]);

  useEffect(() => {
    structureTypesData &&
      setStructureTypes(getSortedStructureTypes(structureTypesData, preferedNodeTypes));
  }, [structureTypesData]);

  useEffect(() => {
    if (selectedStructureType) {
      const selectedPropertyTypes = structureTypes.find(
        (item) => item.code === selectedStructureType
      )?.propertyTypes;

      if (selectedPropertyTypes) {
        setPropertyTypes(selectedPropertyTypes.map((property) => property.propertyType));
      } else {
        setPropertyTypes([]);
      }
    }
  }, [structureTypes, selectedStructureType]);

  useEffect(() => {
    structureLayoutData.selectedTags = [...selectedSystemTags];
    structureLayoutData.newTags = [...newCreatedTags];
  }, [selectedSystemTags, newCreatedTags]);

  const onChangeGlobalReturns = (event: ChangeEvent<HTMLInputElement>) =>
    setCheckedGlobalReturns(event.target.checked);

  // Turning it off is unremarkable; turning it on asks first, because only one
  // location org-wide can hold the marker and it drives depot replenishment.
  const onChangeCentralSupplyWarehouse = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setConfirmCentralSupplyWarehouse(true);
      return;
    }
    setCheckedCentralSupplyWarehouse(false);
  };

  const isValid = () => {
    setHelperText({
      ...helperText,
      nameHelperText: !structureLayoutData.name ? 'Name is required' : '',
    });

    return !isDropDownEmpty(selectedStructureType.toString()) && !!structureLayoutData.name;
  };

  const onHandleSave = async () => {
    const warningsForAddressContacts = getWarningsForAddressContacts(addressForm, contactForm);
    warningsForAddressContacts.forEach((warning) => toast.warning(warning.message));

    if (warningsForAddressContacts.length > 0) {
      return;
    }

    if (!isValid()) {
      setValidate(true);
      return;
    }

    setDisableSave(true);
    const attributes = getNodeAttributes(
      structureAddressDataset,
      propertyTypes,
      structureContactDataset,
      tradingHoursData,
      capacityData,
      structureLayoutData,
      checkedGlobalReturns,
      isSiteSchemaEnabled,
      checkedCentralSupplyWarehouse
    );

    if (isNew) {
      // The create response is read loosely below (id, isAllowedAtRoot) and the
      // .catch() makes it possibly undefined; typing it properly means touching the
      // downstream branches, so leave the shape open here.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newNode: any = await createStructure
        .mutateAsync({
          ...structureLayoutData,
          code: structureLayoutData.name,
          isActive: true,
          parentNodeId: selectedBrandId ? selectedBrandId : parentId || null,
          nodeTypeCode: selectedStructureType,
          attributes: attributes,
        })
        .catch((error) => error && setDisableSave(false));

      if (newNode?.isAllowedAtRoot === false) {
        toast.warning('Parent is not provided, Check if Node Type allowed to be at Root');
        setDisableSave(false);
        return;
      } else {
        await saveTags(newNode.id);

        toast.success('Structure saved');

        // empty states to avoid duplicate displaying of records
        setStructureAddressDataset([]);
        setStructureContactDataset([]);
        setSelectedBrandId('');

        if (pageType === 'service') {
          onSetCreatedLocation && onSetCreatedLocation(newNode.id);
          setMainLocationNode &&
            mainLocationNode &&
            setMainLocationNode({ ...mainLocationNode, id: newNode.id });
          setOpenLocationCreateUpdatePopup && setOpenLocationCreateUpdatePopup(false);
          return;
        }

        setTimeout(() => {
          const url = getUpdateStructurePageUrl(location.pathname, newNode.id);
          navigate(url, { replace: true });
          setDisableSave(false);
        }, 2000);
      }
    } else {
      try {
        await updateStructure.mutateAsync({
          ...structureLayoutData,
          id: id,
          code: structureLayoutData.name,
          isActive: isActive,
          parentNodeId: selectedBrandId ? selectedBrandId : locationId || null,
          attributes: attributes,
          page: 'structure',
        });
      } catch (error) {
        // Prefer whatever the server said: a timeout or validation failure must not
        // be reported as a duplicate Central Supply Warehouse. The singleton hint is
        // only a fallback, for an opaque failure while that box is ticked.
        const serverMessage = (error as AxiosError<{ message?: string }>)?.response?.data?.message;
        toast.error(
          serverMessage ||
            (checkedCentralSupplyWarehouse
              ? CENTRAL_SUPPLY_WAREHOUSE_SAVE_ERROR
              : 'Could not save this location. Please try again.'),
          { duration: 10000 }
        );
        setDisableSave(false);
        return;
      }

      if (pageType === 'service' && locationText) {
        if (locationType === 'main-location' && setLocationText) {
          setLocationText(structureLayoutData.name);

          if (!isActive) {
            setLocationText('');
          }
        } else if (locationType === 'sub-location' && setLocationText) {
          const mainLocationNameText = locationText.split('/')[0];
          const updatedLocationText = mainLocationNameText + '/' + structureLayoutData.name;
          setLocationText(updatedLocationText);

          if (!isActive && setSubLocationNode) {
            setSubLocationNode(undefined);
            setLocationText(mainLocationNameText);
          }
        }
      }

      if (id) {
        await saveTags(id);
      }
      setDisableSave(false);
      toast.success('Structure saved');
    }
    await getNodeData.refetch();
    setParentSelect(false);

    if (pageType === 'service' && !isNew) {
      setOpenLocationCreateUpdatePopup && setOpenLocationCreateUpdatePopup(false);
      return;
    }
  };

  const saveTags = async (nodeId: string) => {
    const existingTags = getSelectedExistingTagsMapped(selectedSystemTags);
    const freshTags = getNewTagsMapped(newCreatedTags);

    await addTagsQuery.mutateAsync({
      entityTypeId: EntityType.TYPE_STRUCTURELOCATIONS,
      entityId: Number(nodeId),
      freshTags,
      existingTags,
    });
  };

  const handleParentSelection = (selectedBrandId: string, value: string) => {
    setLocationId(NaN);
    setLocationName('');
    setParentNodeName('');
    setSelectedBrandId(selectedBrandId);
    setSelectedParenHierarchy(value);
  };

  const onChangeNodeType = (nodeType: NodeType, parentId: string) => {
    if (nodeType === NodeType.Contract) {
      setClientId(parentId);
    } else {
      setContractId(parentId);
    }

    setSelectedParentId(parentId);
    setQueryNodeType(nodeType);
  };

  const handleIsActiveChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsActive(event.target.checked);
  };

  const onAddNewChildStructure = (selectedNodeTypeCode?: string, nodeId?: string) => {
    if (nodeId) {
      const selectedNode = nodeChildren.find((item) => item.id === +nodeId);

      if (selectedNode) {
        setChildNodebasicData({
          id: selectedNode.id,
          name: selectedNode.name,
          isActive: selectedNode.isActive,
        });
        setShowEditPopup(true);
      }
    } else {
      let clientNodeId = clientId?.toString();

      if (!clientNodeId) {
        clientNodeId = getNodeData.data?.clientId?.toString() || '';
      }

      const url = genarateURL(
        location.pathname,
        id,
        structureLayoutData.name,
        selectedNodeTypeCode,
        selectedStructureType,
        structureTypes,
        clientNodeId,
        contractId
      );
      window.open(url, '_self');
    }
  };

  const onChangeChildNodeData = async () => {
    if (!childNodebasicData) return;
    await updateStructureBasicData.mutateAsync(childNodebasicData);
    setNodeChildren((prev) => {
      const node = prev.find((item) => item.id === childNodebasicData.id);

      if (node) {
        node.name = childNodebasicData?.name || node.name;
        node.isActive = childNodebasicData?.isActive || node.isActive;
      }

      return prev;
    });
    setShowEditPopup(false);
    toast.success('Structure saved');
  };

  const onChangeActiveFlag = async (isActive: boolean, id: string) => {
    const selectedNode = nodeChildren.find((item) => item.id === +id);

    if (selectedNode) {
      await updateStructureBasicData.mutateAsync({
        ...selectedNode,
        isActive: isActive,
      });

      setNodeChildren((prev) => {
        const node = prev.find((item) => item.id === +id);

        if (node) {
          node.isActive = isActive;
        }

        return prev;
      });
    }
  };

  // Derived above the early return below, because useStructureActiveTab is a
  // hook and must not sit behind a conditional return.
  const tabContext = {
    isNew,
    isEmbedded: pageType === 'service',
    propertyTypes,
    permissions: structureEditPermissions,
  };
  const sections = getStructureSectionVisibility(tabContext);
  const hasLocationContent = hasStructureLocationContent(tabContext);
  const hasUnsavedChanges = useStructureUnsavedChanges({
    // Everything Save writes. A new slice of form state belongs in this list.
    formState: [
      structureLayoutData,
      structureAddressDataset,
      structureContactDataset,
      tradingHoursData,
      capacityData,
      selectedSystemTags,
      newCreatedTags,
      isActive,
      checkedGlobalReturns,
      checkedCentralSupplyWarehouse,
      selectedStructureType,
      locationId,
      selectedBrandId,
    ],
    baselineKey: getNodeData.dataUpdatedAt,
    isLoaded: !isNew && !!getNodeData.data,
    hasOpenDraft: addressForm.length > 0 || contactForm.length > 0,
  });
  const visibleTabs = getVisibleStructureTabs(tabContext);
  const isTabbed = visibleTabs.length > 0;
  const { activeTab, setActiveTab } = useStructureActiveTab({
    visibleTabs,
    // Gated on the two values `getVisibleStructureTabs` actually reads, not on
    // the queries behind them. The query cache is persisted and restored
    // synchronously at boot, so on a reload the raw responses are back before
    // the effects that derive these from them have run — and one render of that
    // skew was enough for the clamp to see a Details-only tab set and rewrite
    // `?tab=related` to `?tab=details` on every refresh. Both start as
    // placeholders, so identity against them is the honest "not yet" test.
    isTabSetKnown:
      !!getNodeData.data &&
      structureEditPermissions !== SAMPLE_EDIT_STRUCTURE_PERMISSION_SET &&
      propertyTypes[0] !== '',
  });

  if (
    id === 'create' &&
    isUserHasPermission(ActionPermissions.Structure_Create_General, permissions)
  ) {
    isNew = true;
  } else if (
    getNodeData.data &&
    isUserHasPermission(ActionPermissions.Structure_Edit_View_Structure_Location, permissions)
  ) {
    isNew = false;
  }

  if (id !== 'create' && !getNodeData.data) {
    return getNodeData.isFetching ? (
      <LoadingWrapper>
        <Loader color={LoaderColorProps.Success} />
      </LoadingWrapper>
    ) : (
      <NotFoundError></NotFoundError>
    );
  }

  const isEmbedded = tabContext.isEmbedded;
  const structureTypeItems = structureTypes.map((item) => ({ value: item.code, label: item.name }));
  const structureTypeName = structureTypes.find(
    (item) => item.code === selectedStructureType
  )?.name;
  const summaryTags = toSummaryTags(selectedSystemTags, newCreatedTags);
  // The picked location wins over the derived client hierarchy: it is what the
  // user just chose, and it is the only one of the two that links anywhere.
  const parentHierarchy =
    locationId && locationName ? (
      <PermissionLink
        permission="structure.edit.view_structure_location"
        href={`/structure/${locationId}`}
      >
        {locationName}
      </PermissionLink>
    ) : (
      selectedParenHierarchy
    );
  const showParentPicker = isNew || !getNodeData?.data?.parentNodeId;
  // Zones are derived from the address by api-geosocial, so a missing one is
  // only worth reporting once there is an address to have matched against.
  const primaryAddress = structureAddressDataset?.find((address) => address?.isActive);
  const isUnzoned = !isNew && !structureLayoutData.zone && !!primaryAddress;

  /**
   * Puts a suggested suburb onto the address the zone is derived from.
   *
   * Only the first active address is touched — that is the one the banner is
   * describing. The zone itself is derived server-side, so it stays missing
   * until this is saved and refetched; the header's unsaved pill is what asks
   * for the save.
   */
  const applySuggestedSuburb = (locality: string) => {
    setStructureAddressDataset((rows) => {
      let applied = false;
      return rows.map((row) => {
        if (applied || !row?.isActive) return row;
        applied = true;
        return { ...row, suburb: locality };
      });
    });
  };

  // An open address or contact editor is a draft the user has not committed, so
  // Save would return early and look inert.
  const hasOpenAddressOrContactDraft = addressForm.length > 0 || contactForm.length > 0;
  // Which tabs are holding something that blocks Save. Without this a required
  // name, or an uncommitted address draft, would make the Save button look inert
  // while the user is on another tab. Both now live on Details.
  const invalidTabs: StructureTabKey[] = [];
  if (
    helperText?.nameHelperText ||
    (validate && isDropDownEmpty(selectedStructureType.toString())) ||
    hasOpenAddressOrContactDraft
  ) {
    invalidTabs.push('details');
  }

  /**
   * Save, but bring the blocking tab to the front first. `onHandleSave` returns
   * early on an unsaved address/contact draft and on a failed `isValid`, both of
   * which can live on a tab the user cannot currently see.
   */
  const onSaveFromSummaryBar = () => {
    if (isTabbed) {
      const blocking =
        hasOpenAddressOrContactDraft ||
        !structureLayoutData.name ||
        isDropDownEmpty(selectedStructureType.toString())
          ? 'details'
          : undefined;
      if (blocking && blocking !== activeTab && visibleTabs.includes(blocking)) {
        setActiveTab(blocking);
      }
    }
    onHandleSave();
  };
  const canEditCheckboxes = isUserHasPermission(
    ActionPermissions.Configure_Structure_Type_Edit,
    permissions
  );

  return (
    <PageContainer>
      <StructureDetailShell
        embedded={isEmbedded}
        breadcrumbLabel={
          isNew ? 'New structure location' : structureLayoutData.name || `Structure ${id ?? ''}`
        }
        summaryBar={
          <StructureSummaryBar
            name={structureLayoutData.name}
            structureTypeName={structureTypeName}
            structureId={isNew ? undefined : id}
            parentHierarchy={parentHierarchy}
            tags={summaryTags}
            isActive={isActive}
            hideStatus={isNew}
            isUnzoned={isUnzoned}
            zone={structureLayoutData.zone}
            regionType={structureLayoutData.region}
            saveDisabled={disableSave}
            hasUnsavedChanges={hasUnsavedChanges}
            saveLabel={isNew ? 'Create structure' : 'Save changes'}
            onSave={onSaveFromSummaryBar}
            rightSlot={
              !isNew && (
                <PrintLabel
                  entityId={Number(id) || 0}
                  entityType={PrintLabelEntityTypes.Structure}
                />
              )
            }
          />
        }
        tabStrip={
          <StructureTabStrip
            activeTab={activeTab}
            onTabChange={setActiveTab}
            visibleTabs={visibleTabs}
            invalidTabs={invalidTabs}
          />
        }
      >
        <StructureTabSection
          tabKey="details"
          activeTab={activeTab}
          tabbed={isTabbed}
          visible={sections.details}
        >
          <StructureDetailsTabBody
            propertyTypes={propertyTypes}
            isNew={isNew}
            structureEditPermissions={structureEditPermissions}
            hasLocationContent={hasLocationContent}
            detailsPanel={
              <Panel title="Details" overflow="visible">
                {showParentPicker && (
                  <MuiBox sx={{ mb: 2 }}>
                    <MuiTypography sx={{ fontSize: '13px', fontWeight: 600, mb: 0.75 }}>
                      Parent
                    </MuiTypography>
                    <ToggleButtonGroup
                      size={SizeProps.Small}
                      color={ToogleButtonGroupColor.Standard}
                      value={parentType}
                      onChange={handleChange}
                      exclusive={!parentId}
                      activeTab={getActiveToggleButton(locationId, selectedParenHierarchy)}
                      items={[
                        {
                          value: ParentSelectionType.None,
                          label: 'None',
                          icon: <StyledCloseIcon />,
                          onClick: (value) => {
                            if (value) {
                              setClearClientHierarchy(true);
                              setLocationId(NaN);
                              setLocationName('');
                              setParentNodeName('');
                              setSelectedBrandId('');
                              setSelectedParenHierarchy('');
                              setClients([]);
                              setContracts([]);
                              setBrands([]);
                            }
                          },
                        },
                        {
                          value: ParentSelectionType.Client,
                          label: 'Client',
                          subLabel:
                            parentType === ParentSelectionType.None
                              ? ''
                              : parentId
                                ? ''
                                : selectedParenHierarchy,
                          icon: <StyledPeopleAltOutlinedIcon />,
                          onClick: (value) => {
                            if (value === NodeType.Client && !parentName) {
                              setOpenParentSelector(true);
                              getStructuresByTypeCategoryCodeAndTypeCode.refetch();
                            }
                          },
                        },
                        {
                          value: ParentSelectionType.Search,
                          label: 'Search',
                          subLabel:
                            parentType === ParentSelectionType.None
                              ? ''
                              : locationName
                                ? locationName
                                : parentNodeName,
                          icon: <StyledSearchIcon />,
                          onClick: (value) => {
                            if (value && !parentName) {
                              setOpenLocationSearch(true);
                            }
                          },
                        },
                      ]}
                    />
                  </MuiBox>
                )}

                {/* The type is fixed once a structure exists, and the summary bar
                  already reads "<name> : <type>". A permanently disabled select
                  spent a full field slot restating it, so the picker only
                  appears where it can actually be used. */}
                {isNew && (
                  <MuiBox sx={{ mb: 2 }}>
                    <SelectMenu
                      id="type-selection"
                      label="Structure Type"
                      selectedValue={selectedStructureType}
                      required
                      items={structureTypeItems}
                      validate={validate}
                      optionalLabelEnabled
                      onChange={(structureType) => setSelectedStructureType(structureType)}
                    />
                  </MuiBox>
                )}

                <StructureLayout
                  flush
                  grouped
                  isNew={isNew}
                  helperText={helperText}
                  setHelperText={setHelperText}
                  structureLayoutData={structureLayoutData}
                  structureLayoutSchemaType={structureLayoutSchemaType}
                  onChange={(structureData: StructureLayoutData) => {
                    setStructureLayoutData(structureData);
                    if (structureData.name) {
                      selectedStructureType && setDisableSave(false);
                    }
                  }}
                  allTags={allTags}
                  renderNotes={true}
                  enableSiteSchemaType={isSiteSchemaEnabled}
                  enableReferenceNumbers={isReferenceNumbersEnabled}
                  onChangeSelectedTags={(value) => {
                    setSelectedSystemTags([...value]);
                  }}
                  onChangeNewTags={(value) => {
                    setNewCreatedTags([...value]);
                  }}
                  assignedSelectedTags={[...selectedSystemTags]}
                  assignedNewTags={[...newCreatedTags]}
                  structureEditPermissions={structureEditPermissions}
                />
              </Panel>
            }
            statusPanel={
              !isNew && (
                <StructureCapabilitiesPanel
                  propertyTypes={propertyTypes}
                  isActive={isActive}
                  canEditActive={!!structureEditPermissions?.hasActiveInactiveEditPermission}
                  onActiveChange={handleIsActiveChange}
                  showGlobalReturns={
                    propertyTypes.includes(PropertyType.GlobalReturns) && canEditCheckboxes
                  }
                  globalReturnsChecked={checkedGlobalReturns}
                  canEditGlobalReturns={!!structureEditPermissions.hasGlobalReturnEditPermission}
                  onGlobalReturnsChange={onChangeGlobalReturns}
                  showCentralSupplyWarehouse={
                    propertyTypes.includes(PropertyType.CentralSupplyWarehouse) && canEditCheckboxes
                  }
                  centralSupplyWarehouseChecked={checkedCentralSupplyWarehouse}
                  onCentralSupplyWarehouseChange={onChangeCentralSupplyWarehouse}
                />
              )
            }
            banner={
              isUnzoned ? (
                <StructureZoneBanner
                  suburb={primaryAddress?.suburb}
                  postcode={primaryAddress?.postCode}
                  state={primaryAddress?.stateName}
                  country={primaryAddress?.countryName}
                  onApplySuburb={
                    structureEditPermissions?.hasContractAddressEditPermission
                      ? applySuggestedSuburb
                      : undefined
                  }
                />
              ) : undefined
            }
            unmatchedSuburb={isUnzoned ? primaryAddress?.suburb : undefined}
            addressDataset={structureAddressDataset}
            setAddressDataset={setStructureAddressDataset}
            addressFormData={addressData}
            setAddressFormData={setAddressData}
            editAddressData={editAddressData}
            setEditAddressData={setEditAddressData}
            addressForm={addressForm}
            setAddressForm={setAddressForm}
            contactDataset={structureContactDataset}
            setContactDataset={setStructureContactDataset}
            contactFormData={contactData}
            setContactFormData={setContactData}
            editContactData={editContactData}
            setEditContactData={setEditContactData}
            contactForm={contactForm}
            setContactForm={setContactForm}
            tradingHoursData={tradingHoursData}
            setTradingHoursData={setTradingHoursData}
            capacityData={capacityData}
            setCapacityData={setCapacityData}
            capacityDefaultValue={capacityDefaultValue}
          />
        </StructureTabSection>

        <StructureTabSection
          tabKey="parts"
          activeTab={activeTab}
          tabbed={isTabbed}
          visible={sections.parts && !isNew && !!id}
        >
          {id && <PartAccordianFragment nodeId={parseInt(id)} />}
        </StructureTabSection>

        <StructureTabSection
          tabKey="related"
          activeTab={activeTab}
          tabbed={isTabbed}
          visible={sections.related}
        >
          <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            {structureEditPermissions?.hasViewChildrenEditPermission && (
              <StructureChild
                editLabel="Edit"
                switchLabel="Active"
                childrenData={nodeChildren}
                hasViewChildrenEditPermission={
                  structureEditPermissions?.hasViewChildrenEditPermission
                }
                onClick={onAddNewChildStructure}
                onChangeActiveFlag={onChangeActiveFlag}
              />
            )}
            {id &&
              (propertyTypes.includes(PropertyType.Service) ||
                propertyTypes.includes(PropertyType.ServicesAssignment)) &&
              structureEditPermissions?.hasViewServicesEditPermission && (
                <ServiceAccordianFragment nodeId={parseInt(id)} permissions={permissions} />
              )}
            {id && structureEditPermissions?.hasViewShipmentsEditPermission && (
              <ShipmentAccordianFragment nodeId={parseInt(id)} />
            )}
          </MuiBox>
        </StructureTabSection>

        <StructureTabSection
          tabKey="admin"
          activeTab={activeTab}
          tabbed={isTabbed}
          visible={sections.admin && !isNew && !!id}
        >
          {id && (
            <LocationMergeAccordianFragment
              mergeNodeId={parseInt(id)}
              nodeTypeCode={selectedStructureType}
            />
          )}
        </StructureTabSection>
      </StructureDetailShell>
      <Modal
        open={showEditPopup}
        onClose={() => setShowEditPopup(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <ModalBox>
          <CloseIcon handleClose={() => setShowEditPopup(false)} />
          <ModalContent>
            <TextField
              label="Name"
              value={childNodebasicData?.name}
              onChange={(value: string) => {
                setChildNodebasicData((prev) => {
                  if (prev) {
                    return { ...prev, name: value };
                  }
                });
              }}
            ></TextField>
          </ModalContent>
          <ModalFooter>
            <Button
              variant={ButtonVariantProps.Primary}
              startIcon={<Done />}
              onClick={onChangeChildNodeData}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalBox>
      </Modal>
      <BrandSelector
        open={openParentSelector}
        onChangeNodeType={onChangeNodeType}
        handleClose={() => {
          setOpenParentSelector(false);
        }}
        handleParentSelection={handleParentSelection}
        isActiveClear={false}
        clients={clients}
        contracts={contracts}
        brands={brands}
        clearFlag={clearClientHierarchy}
        resetClearFlag={() => setClearClientHierarchy(false)}
      />
      <Modal
        open={openLocationSearch}
        onClose={() => {
          setOpenLocationSearch(false);
        }}
      >
        <LocationSearchBox>
          <LiveSearchBox
            title="Location Search"
            timeOffset={400}
            value={locationName || ''}
            onClearValue={() => {
              setClearClientHierarchy(true);
              setLocationId(NaN);
              setLocationName('');
              setParentNodeName('');
              setSelectedBrandId('');
              setSelectedParenHierarchy('');
              setClients([]);
              setContracts([]);
              setBrands([]);
            }}
            renderItem={(props: React.HTMLAttributes<HTMLLIElement>, option: LocationNodeData) => {
              return (
                <LiveSearchListItem {...props}>
                  <LocationSearchItem data={option} />
                </LiveSearchListItem>
              );
            }}
            onChange={(obj: LocationNodeData) => {
              setClearClientHierarchy(true);
              setSelectedBrandId('');
              setSelectedParenHierarchy('');
              setLocationName(obj?.nodeName || obj?.name);
              setLocationId(obj?.id);
              setOpenLocationSearch(false);
              setParentSelect(true);
            }}
            onApiInvoke={async (name) => {
              const hasCode = getHasCodeForNodeSearch(name);
              const response = await searchNodeByParentId.mutateAsync({
                selectedParentId: selectedBrandId,
                name,
                hasCode: hasCode,
              });
              const responseWithoutCurrentNode = response.filter(
                (result) => isNew || (id && result.id !== Number(id))
              );
              return responseWithoutCurrentNode;
            }}
          />
        </LocationSearchBox>
      </Modal>
      <ConfirmationPopup
        open={confirmCentralSupplyWarehouse}
        mainHeader={CENTRAL_SUPPLY_WAREHOUSE_CONFIRM_TITLE}
        description={CENTRAL_SUPPLY_WAREHOUSE_CONFIRM_DESCRIPTION}
        leftButtonName="Cancel"
        rightButtonName="Set as Central Supply Warehouse"
        leftButtonOnClick={() => setConfirmCentralSupplyWarehouse(false)}
        rightButtonOnClick={() => {
          setCheckedCentralSupplyWarehouse(true);
          setConfirmCentralSupplyWarehouse(false);
        }}
      />
    </PageContainer>
  );
};
